import { SlideshowSelectEvent } from '@theme/events';

/**
 * Key Highlights overlay for PDP media gallery.
 * Mobile: slide 1 closed, slide 2 auto-open, slide 3+ auto-close.
 * Desktop: click-only toggle on the 2nd grid image.
 */
class KeyHighlights extends HTMLElement {
  /** @type {boolean} */
  #open = false;

  /** @type {HTMLElement | null} */
  #slideshow = null;

  /** @type {HTMLButtonElement | null} */
  #toggle = null;

  /** @type {(event: Event) => void} */
  #boundSlideshowSelect = this.#handleSlideshowSelect.bind(this);

  connectedCallback() {
    this.#toggle = this.querySelector('[data-key-highlights-toggle]');
    if (!this.#toggle) return;

    this.#toggle.addEventListener('click', this.#handleToggleClick);
    this.#toggle.addEventListener('pointerdown', this.#stopMediaGesture);

    const panel = this.querySelector('.key-highlights__panel');
    panel?.addEventListener('click', this.#stopMediaGesture);
    panel?.addEventListener('pointerdown', this.#stopMediaGesture);

    if (this.dataset.variant === 'mobile') {
      this.#bindSlideshow();
    }
  }

  disconnectedCallback() {
    this.#toggle?.removeEventListener('click', this.#handleToggleClick);
    this.#toggle?.removeEventListener('pointerdown', this.#stopMediaGesture);

    const panel = this.querySelector('.key-highlights__panel');
    panel?.removeEventListener('click', this.#stopMediaGesture);
    panel?.removeEventListener('pointerdown', this.#stopMediaGesture);

    this.#slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#boundSlideshowSelect);
  }

  #bindSlideshow() {
    const host = this.closest('.media-gallery__key-highlights-host');
    this.#slideshow = host?.querySelector('slideshow-component') ?? null;
    if (!this.#slideshow) return;

    this.#slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#boundSlideshowSelect);

    const slideshowWithCurrent = /** @type {HTMLElement & { current?: number }} */ (this.#slideshow);
    const currentIndex =
      typeof slideshowWithCurrent.current === 'number'
        ? slideshowWithCurrent.current
        : Number(this.#slideshow.getAttribute('initial-slide') || 0);

    this.#applySlideIndex(currentIndex);
  }

  /**
   * @param {Event} event
   */
  #handleSlideshowSelect(event) {
    const detail = /** @type {{ detail?: { index?: number } }} */ (event).detail;
    if (!detail || typeof detail.index !== 'number') return;
    this.#applySlideIndex(detail.index);
  }

  /**
   * @param {number} index
   */
  #applySlideIndex(index) {
    if (index === 1) {
      this.#setOpen(true);
      return;
    }

    this.#setOpen(false);
  }

  /**
   * @param {Event} event
   */
  #stopMediaGesture = (event) => {
    event.stopPropagation();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleToggleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#setOpen(!this.#open);
  };

  /**
   * @param {boolean} open
   */
  #setOpen(open) {
    this.#open = open;
    this.classList.toggle('is-open', open);
    this.#toggle?.setAttribute('aria-expanded', String(open));

    const panel = this.querySelector('.key-highlights__panel');
    if (!panel) return;

    if (open) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }
  }
}

if (!customElements.get('key-highlights')) {
  customElements.define('key-highlights', KeyHighlights);
}
